import styles from "./PlannerEventUserItem.module.scss";
import { IPlannerEventUserModel } from "../models/plannerEventUserModel";
import { getStatus } from "../utils/PlannerEventStatusUtility";
import { humanizePaymentType } from "../../../../common/utilities/paymentUtility";
import { classNames } from "primereact/utils";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { Tag } from "../../../../common/components/common/Tag/Tag";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import {
  CurrencyType,
  PaymentTypes,
  UserEventStatus,
} from "../../../../common/enums";
import { PriceDisplay } from "../../../../common/components/PriceDisplay/PriceDisplay";
import { usePlannerEventUserItemNote } from "../hooks/usePlannerEventUserItemNote";
import { getPlanLabel } from "../utils/plannerEventPlanLabelUtility";

interface IPlannerEventUserItemProps {
  isPassed: boolean;
  user?: IPlannerEventUserModel;
  isLoading: boolean;
  actionButtons?: React.ReactNode;
  setSelectedUserEventDetailsId: (userEventId: number) => void;
}

export const PlannerEventUserItem = ({
  isPassed,
  user,
  isLoading,
  actionButtons,
  setSelectedUserEventDetailsId,
}: IPlannerEventUserItemProps) => {
  const onUserClick = () => {
    setSelectedUserEventDetailsId(user?.userEventId ?? 0);
  };

  const { notePreview, extraNotesCount } = usePlannerEventUserItemNote(user);

  const {
    color: statusColor,
    label: statusLabel,
    additionalTag,
  } = getStatus(
    isPassed,
    user?.status,
    user?.isCancelled,
    user?.pricePaidInEur
  );

  const basePaymentTypeLabel = humanizePaymentType(
    user?.paymentType,
    user?.paymentPlanId,
    user?.planName
  );

  // The currency is no longer appended here — the amount itself carries it.
  const paymentTypeLabel = basePaymentTypeLabel;

  const planLabel = getPlanLabel(user?.currentPlan);

  const visitsUserLabel =
    !!user?.initialPlanVisits && !!user?.visitsUsed
      ? `${user?.visitsUsed}/${user?.initialPlanVisits}`
      : "N/A";

  return (
    <div className={classNames(styles.item)} onClick={onUserClick}>
      <div className={styles.item_content}>
        <div className={styles.item_content_left}>
          <div className={styles.item_content_left_title}>
            <SkeletonItem
              isLoading={isLoading}
              value={
                <>
                  {user?.name}{" "}
                  <RenderWhen condition={!!user?.hasClassFlag}>
                    <i
                      className={classNames(
                        "pi pi-flag-fill",
                        styles.item_content_classFlag,
                      )}
                      data-testid="class-flag"
                      aria-label="Флаг за този клас"
                      title="Флаг за този клас"
                    />{" "}
                  </RenderWhen>
                  {user?.isUnreliable ? (
                    <span className={styles.item_content_unreliable}>
                      - НЕРЕДОВЕН
                    </span>
                  ) : (
                    ""
                  )}
                  <RenderWhen condition={!!planLabel}>
                    <span
                      className={classNames(styles.item_content_plan, {
                        [styles.item_content_plan_expired]:
                          planLabel?.isExpired,
                      })}
                      data-testid="plan-label"
                    >
                      {" "}
                      · {planLabel?.text}
                    </span>
                  </RenderWhen>
                </>
              }
              width={150}
            />
          </div>
          <SkeletonItem
            isLoading={isLoading}
            width={250}
            value={
              <div className={styles.item_content_left_sub}>
                ID: {user?.id}
                <Tag label={statusLabel} backgroundColor={statusColor} />
                <RenderWhen condition={!!additionalTag}>
                  <Tag
                    label={additionalTag?.label ?? ""}
                    backgroundColor={additionalTag?.color ?? ""}
                  />
                </RenderWhen>
                <RenderWhen condition={!!paymentTypeLabel}>
                  <Tag label={paymentTypeLabel!} backgroundColor="#cedef9" />
                </RenderWhen>
              </div>
            }
          />
        </div>

        <div className={styles.item_content_right}>{actionButtons}</div>
      </div>

      <div className={styles.item_content_footer}>
        <SkeletonItem
          isLoading={isLoading}
          width={200}
          value={
            <div className={styles.item_content_footer_left}>
              <RenderWhen condition={!!user?.email}>
                <div>
                  <span className={styles.item_content_footer_left_name}>
                    Email:{" "}
                  </span>
                  <span className={styles.item_content_footer_left_value}>
                    {user?.email}
                  </span>
                </div>
              </RenderWhen>
              <RenderWhen condition={!!user?.phoneNumber}>
                <div>
                  <span className={styles.item_content_footer_left_name}>
                    Телефон:{" "}
                  </span>
                  <span className={styles.item_content_footer_left_value}>
                    {user?.phoneNumber}
                  </span>
                </div>
              </RenderWhen>
            </div>
          }
        />
        <SkeletonItem
          width="20%"
          isLoading={isLoading}
          value={
            <div className={styles.item_content_footer_right}>
              <RenderWhen
                condition={
                  !!user?.pricePaid &&
                  user?.paymentType !== PaymentTypes.Multisport &&
                  (user.status === UserEventStatus.Arrived ||
                    user.status === UserEventStatus.OnlyPaid ||
                    user.status === UserEventStatus.Confirmed)
                }
              >
                <span className={styles.item_content_footer_right_pricePaid}>
                  <PriceDisplay
                    minPrice={user?.pricePaid ?? 0}
                    currencyId={user?.currencyId}
                  />
                </span>
              </RenderWhen>
              <span className={styles.item_content_footer_right_visits}>
                <RenderWhen
                  condition={
                    user?.paymentType === PaymentTypes.Plan &&
                    !!user.visitsUsed &&
                    !!user?.initialPlanVisits
                  }
                >
                  | {visitsUserLabel}
                </RenderWhen>
              </span>
            </div>
          }
        />
      </div>

      <RenderWhen condition={!isLoading && !!notePreview}>
        <div
          className={styles.item_content_note}
          data-testid="reservation-note"
        >
          <span className={styles.item_content_note_text}>
            <span className={styles.item_content_note_label}>Бележка: </span>
            {notePreview}
          </span>
          <RenderWhen condition={extraNotesCount > 0}>
            <span className={styles.item_content_note_more}>
              +{extraNotesCount}
            </span>
          </RenderWhen>
        </div>
      </RenderWhen>
    </div>
  );
};
